import { _ as __rest } from './tslib.es6-2af8cb53.js';
import { s as siteType } from './request.constants-deb285e0.js';
import { h as hasHttpOrHttps, f as fetchers } from './lookups-705fb53e.js';
import { i as isHostnameContainsCC } from './locale-86283e50.js';
import { g as getData, B as getSprykerBaseQueryParams } from './utils-15ad90ec.js';
import { c as currency } from './currency-b61904e0.js';

const AUTO_DETECT_ZIP_CODE_SESSION_STORAGE_KEY = 'STATDEMANDBASE';
const AUTO_DETECT_ZIP_CODE_PROPERTY_NAME = 'registry_zip_code';
const USER_ZIP_CODE_LOCAL_STORAGE_KEY = 'userZipCode';
let isGeolocated = false;
function isZipCodeGeolocated() {
  return isGeolocated;
}
function isZipCodeValid(pattern, code) {
  return pattern.test(code);
}
function removeZipCodeFromStorage() {
  window.localStorage.removeItem(USER_ZIP_CODE_LOCAL_STORAGE_KEY);
}
function saveZipCodeToStorage(countryLanguageCode, code) {
  const value = {};
  value[countryLanguageCode] = code;
  window.localStorage.setItem(USER_ZIP_CODE_LOCAL_STORAGE_KEY, JSON.stringify(value));
}
function getValueFromStorage(storage, key, namespace) {
  const storageObj = storage.getItem(key);
  try {
    const parsedObj = JSON.parse(storageObj);
    const value = parsedObj[namespace];
    return value ? value.toString() : null;
  }
  catch (e) {
    return null;
  }
}
function restoreZIPCodeValue(countryLanguageCode, length) {
  const localeZIPCode = getValueFromStorage(window.localStorage, USER_ZIP_CODE_LOCAL_STORAGE_KEY, countryLanguageCode);
  return localeZIPCode ? localeZIPCode.slice(0, length) : null;
}
function getZipCode(params) {
  const { countryLanguageCode, length, detect, pattern } = params;
  let code = restoreZIPCodeValue(countryLanguageCode, length);
  if (code && !isZipCodeValid(pattern, code)) {
    removeZipCodeFromStorage();
    code = null;
  }
  isGeolocated = false;
  if (!code && detect) {
    code = getValueFromStorage(window.sessionStorage, AUTO_DETECT_ZIP_CODE_SESSION_STORAGE_KEY, AUTO_DETECT_ZIP_CODE_PROPERTY_NAME);
    if (code) {
      isGeolocated = true;
    }
  }
  return isZipCodeValid(pattern, code) ? code : null;
}

const applyPriceTemplate = (price, template, placeholder = '#PartnerPrice#') => template.replace(placeholder, price.toString());
const getMinimalPrice = (distributors) => {
  const distributorsWithPrices = distributors.filter((distributor) => {
    return distributor.price > 0;
  });
  if (distributorsWithPrices.length) {
    const distributorsSortedByPrice = distributorsWithPrices.sort((a, b) => a.price - b.price);
    return distributorsSortedByPrice[0].price;
  }
};
const getPrices = (productsDistributorsInfo, { template, locale }) => {
  return Object.keys(productsDistributorsInfo).reduce((result, commercialReference) => {
    const price = getMinimalPrice(productsDistributorsInfo[commercialReference]);
    if (!price) {
      return result;
    }
    result[commercialReference] = applyPriceTemplate(currency.format(price, { locale }), template);
    return result;
  }, {});
};

const RETAILER_PRODUCT_IN_STOCK = 'InStock';
function getBuyOnlineAvailability(products, distributorsInfo) {
  return products.reduce((result, product) => {
    var _a;
    result[product.commercialReference] = Boolean((_a = distributorsInfo[product.commercialReference]) === null || _a === void 0 ? void 0 : _a.length);
    return result;
  }, {});
}
function getNotificationMessage(zipCode, distributorsLength, { options: { useZipCode, enableEmptyZipCode }, messages: { allDistributorsShown, autoDetectedZipCode }, }) {
  if (useZipCode && distributorsLength && isZipCodeGeolocated()) {
    return autoDetectedZipCode;
  }
  if (useZipCode && !zipCode && enableEmptyZipCode && distributorsLength) {
    return allDistributorsShown;
  }
}
function getDistributors(productsDistributorsInfo, id, options) {
  if (productsDistributorsInfo && id in productsDistributorsInfo) {
    const distributors = productsDistributorsInfo[id];
    const { priceTemplate, priceLocale } = options;
    return distributors.map((_a) => {
      var { price } = _a, rest = __rest(_a, ["price"]);
      return Object.assign(Object.assign({}, rest), { price, priceFormatted: applyPriceTemplate(currency.format(price, { locale: priceLocale }), priceTemplate) });
    });
  }
  return [];
}
function getAllDistributors(id, productsDistributorsInfo, additionalProductsDistributorsInfo, options) {
  let distributors = getDistributors(productsDistributorsInfo, id, options);
  if (additionalProductsDistributorsInfo) {
    const additionalDistributors = getDistributors(additionalProductsDistributorsInfo, id, options).map((distributor) => (Object.assign(Object.assign({}, distributor), { showPrice: false })));
    distributors = [...additionalDistributors, ...distributors];
  }
  return distributors;
}
const isDIYRetailer = ({ Key, Value }) => Key === 'IsDIY' && Value === 'true';
const retailerShouldBeVisible = (metaData, siteType$1) => {
  if (siteType$1 === siteType.B2B && Array.isArray(metaData) && metaData.some(isDIYRetailer)) {
    return false;
  }
  if (siteType$1 === siteType.B2C && Array.isArray(metaData) && !metaData.some(isDIYRetailer)) {
    return false;
  }
  return true;
};
function getProductsDistributorsInfo(products, showPrice, stockLabels, siteType) {
  return products.reduce((result, product) => {
    result[product.ProductId] = product.RetailerProducts.reduce((result, { RetailerId, RetailerName, RetailerLogoUrl, StockPrices, ClickThruUrl, RetailerCurrency, RetailerSite, RetailerMetaData, }) => {
      if (!retailerShouldBeVisible(RetailerMetaData, siteType)) {
        return result;
      }
      const defaultStockPrice = StockPrices && StockPrices.find((stockPriceItem) => stockPriceItem.IsDefault);
      const isAvailable = defaultStockPrice
        ? defaultStockPrice.Stock === RETAILER_PRODUCT_IN_STOCK
        : false;
      result.push(Object.assign(Object.assign({ id: RetailerId, name: RetailerName, logoUrl: RetailerLogoUrl, isAvailable, isFavorite: null, retailerSite: RetailerSite, buyUrl: ClickThruUrl, showPrice, stockLabel: isAvailable ? stockLabels.inStock : stockLabels.outOfStock }, (showPrice && defaultStockPrice && { price: defaultStockPrice.Price })), (showPrice && defaultStockPrice && { currency: RetailerCurrency })));
      return result;
    }, []);
    return result;
  }, {});
}
function getUrl({ feedUrl, zipCode, productIds, }) {
  const zipCodeParametr = zipCode ? `&cl=${zipCode}` : '';
  return `${feedUrl}${zipCodeParametr}&pid=${productIds}`;
}
function getProductsDataFeed({ feedUrl, zipCode, productIds, }) {
  const url = getUrl({ feedUrl, zipCode, productIds });
  return getData(false, url).then((response) => response);
}
function getProductIds(products) {
  return products.reduce((summ, product) => `${summ}${summ ? ',' : ''}${product.commercialReference}`, '');
}
function normalizeZipCodePattern(pattern) {
  return new RegExp(pattern);
}
function startSearch({ feedUrl, commercialReference, zipCode, stockLabels, showPrice, siteType, }) {
  return getProductsDataFeed({ feedUrl, zipCode, productIds: commercialReference }).then((productsDataFeed) => {
    const distributorProductsInfo = getProductsDistributorsInfo(productsDataFeed, showPrice, stockLabels, siteType);
    return {
      distributorProductsInfo,
      productsDataFeed,
    };
  });
}
function getCommerceConnectorSettings(apiHost, localeCode) {
  const isValidHost = hasHttpOrHttps(apiHost);
  if (isValidHost && !localeCode) {
    const parsedURL = new URL(apiHost);
    const path = parsedURL.pathname.endsWith('/') ? parsedURL.pathname.slice(0, -1) : parsedURL.pathname;
    const { contextRoot, countryCode, languageCode } = getSprykerBaseQueryParams(path);
    return fetchers.getCommerceConnector({
      host: parsedURL.origin,
      contextRoot,
      countryCode: !isHostnameContainsCC() ? countryCode : null,
      languageCode,
    });
  }
  const { contextRoot, countryCode, languageCode } = getSprykerBaseQueryParams(localeCode || apiHost);
  return fetchers.getCommerceConnector({
    host: isValidHost ? apiHost : '',
    contextRoot,
    countryCode: !isHostnameContainsCC() ? countryCode : null,
    languageCode,
  });
}

export { getCommerceConnectorSettings as a, getProductIds as b, getZipCode as c, getBuyOnlineAvailability as d, getPrices as e, startSearch as f, getNotificationMessage as g, getAllDistributors as h, isZipCodeValid as i, getProductsDataFeed as j, normalizeZipCodePattern as n, saveZipCodeToStorage as s };
